import type { ReactNode } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@shared/components';
import { PermissionGuard } from '@/features/auth';
import type { HolderFieldKey, ResolvedHolder, ResolvedHolderField } from '../utils/holderResolution';
import type { HolderSource } from '../hooks/useAgreementHolder';

interface Props {
    holder: ResolvedHolder;
    source: HolderSource;
    canChangeContactDetails: boolean;
    /** The change action, supplied by the host so it can be a link or a button. */
    action?: ReactNode;
    canChangeName?: boolean;
    nameAction?: ReactNode;
    /** The amendment log (Epic 3.7 slice C1b). */
    log?: ReactNode;
}

const headingId = 'agreement-customer-heading';

const SNAPSHOT_LABEL = 'Showing the details recorded at sale';

/** Grouped the way a customer record reads, and the way the change form is laid
 *  out, so viewing and editing correspond. */
const GROUPS: Array<{ title: string; keys: HolderFieldKey[] }> = [
    { title: 'Who they are', keys: ['name', 'businessName', 'vatNumber'] },
    { title: 'How to reach them', keys: ['email', 'phone', 'mobilePhone'] },
    { title: 'Where they are', keys: ['address'] },
];

/** Presentational. The three-state `source` and the change affordances are both
 *  decided by useAgreementHolder; this panel holds no rule of its own. */
export function AgreementCustomerPanel({
    holder,
    source,
    canChangeContactDetails,
    action,
    canChangeName = false,
    nameAction,
    log,
}: Props) {
    const byKey = new Map(holder.fields.map((field) => [field.key, field]));
    const groups = GROUPS.map((group) => ({
        ...group,
        fields: group.keys
            .map((key) => byKey.get(key))
            .filter((field): field is ResolvedHolderField => field?.current !== undefined),
    })).filter((group) => group.fields.length > 0);

    return (
        <Card>
            <CardHeader className="flex items-center justify-between gap-4">
                <CardTitle>
                    <span id={headingId}>Customer</span>
                </CardTitle>
                <div className="flex flex-wrap items-center justify-end gap-2">
                    {canChangeName && nameAction && (
                        <PermissionGuard permission={{ resource: 'customer', action: 'edit' }}>
                            <PermissionGuard permission={{ resource: 'customer', action: 'change_name' }}>
                                {nameAction}
                            </PermissionGuard>
                        </PermissionGuard>
                    )}
                    {canChangeContactDetails && action && (
                        <PermissionGuard permission={{ resource: 'customer', action: 'edit' }}>
                            {action}
                        </PermissionGuard>
                    )}
                </div>
            </CardHeader>
            <CardContent>
                <section aria-labelledby={headingId} className="space-y-6">
                    {/* The label is a claim about which record is on screen, so it is
                        shown only once the answer is in. */}
                    {source === 'snapshot' && (
                        <p className="text-xs text-mp-muted">{SNAPSHOT_LABEL}</p>
                    )}

                    <div aria-busy={source === 'pending'}>
                        {groups.length === 0 ? (
                            <p className="text-sm text-mp-muted">
                                No customer details were recorded on this agreement.
                            </p>
                        ) : (
                            <div className="divide-y divide-mp-border">
                                {groups.map((group) => (
                                    <Group key={group.title} title={group.title} fields={group.fields} />
                                ))}
                            </div>
                        )}
                    </div>

                    {log}
                </section>
            </CardContent>
        </Card>
    );
}

function Group({ title, fields }: { title: string; fields: ResolvedHolderField[] }) {
    return (
        <div className="py-5 first:pt-0 last:pb-0">
            <h3 className="mb-4 text-xs font-semibold uppercase tracking-wide text-mp-muted">
                {title}
            </h3>
            <dl className="grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2">
                {fields.map((field) => (
                    <Field key={field.key} field={field} />
                ))}
            </dl>
        </div>
    );
}

function Field({ field }: { field: ResolvedHolderField }) {
    const isAddress = field.key === 'address';

    return (
        <div className={isAddress ? 'sm:col-span-2' : undefined}>
            <dt className="mb-1 text-sm text-mp-muted">{field.label}</dt>
            <dd className="text-sm font-medium text-mp-foreground">
                {isAddress ? (
                    <address className="not-italic">
                        {(field.currentLines ?? []).map((line) => (
                            <span key={line} className="block">
                                {line}
                            </span>
                        ))}
                    </address>
                ) : (
                    field.current
                )}
                {field.changed && (
                    <p className="mt-1 text-xs font-normal text-mp-muted">
                        As sold: {isAddress ? (field.asSoldLines ?? []).join(', ') : field.asSold}
                    </p>
                )}
            </dd>
        </div>
    );
}
